// Attendance-коды ведёт офис в админке (Timesheet → Options → Attendance Codes),
// айпад получает список при синхронизации и хранит в IndexedDB. Экран читает его
// синхронно, поэтому список держим в памяти: loadAttendCodes поднимает его до
// отрисовки сетки. До первой синхронизации работает встроенный список.
const LEGACY_KEY = 'timesheetAttendCodes';

export const NIGHT_CODE = 'Night work';

const ABSENCE_TEXT = 'Absence day. Hours can stay at 0.';
const NIGHT_TEXT = 'Indigo NW badge in a cell = this WORKER is on night shift that day (payroll gets "Night work"; one worker can be on days, another on nights on the same job). HOW TO SET: long-press the worker’s cell → Attendance code → "Night work". It auto-applies to that worker’s remaining days of the week; any day can be changed back.';

const DEFAULTS = [
    { value: 'No Call No Show', short: 'NC', description: ABSENCE_TEXT, night: false, active: true },
    { value: 'Excused', short: 'EX', description: ABSENCE_TEXT, night: false, active: true },
    { value: 'Unexcused', short: 'UN', description: ABSENCE_TEXT, night: false, active: true },
    { value: 'Termed - ADP entered', short: 'TM', description: ABSENCE_TEXT, night: false, active: true },
    { value: NIGHT_CODE, short: 'NW', description: NIGHT_TEXT, night: true, active: true },
];

let cache = null;
let loading = null;

function all() {
    return cache || DEFAULTS;
}

// db.jsx подключаем по месту: списку кодов база нужна только для чтения и записи.
async function database() {
    const { initializeDB } = await import('../config/db');
    const { ATTEND_CODES_STORE } = await import('../config/constants');
    return { db: await initializeDB(), store: ATTEND_CODES_STORE };
}

async function writeCodes(list) {
    const { db, store } = await database();
    const tx = db.transaction(store, 'readwrite');
    await tx.store.clear();
    await Promise.all(list.map((c, order) => tx.store.put({ ...c, order })));
    await tx.done;
}

/** Поднять список из IndexedDB в память. Старый список из localStorage переезжает в базу. */
export function loadAttendCodes() {
    if (!loading) {
        loading = (async () => {
            try {
                const { db, store } = await database();
                const rows = await db.getAll(store);
                if (rows.length && !cache) {
                    cache = rows.sort((a, b) => a.order - b.order);
                }
                const legacy = localStorage.getItem(LEGACY_KEY);
                if (legacy !== null) {
                    localStorage.removeItem(LEGACY_KEY);
                    const old = JSON.parse(legacy);
                    if (!rows.length && !cache && Array.isArray(old) && old.length) {
                        cache = old;
                        await writeCodes(old);
                    }
                }
            } catch (e) {
                console.warn('[AttendCodes] load failed', e);
            }
        })();
    }
    return loading;
}

/** Список с сервера. Пишем в базу, только если он изменился. */
export async function saveAttendCodes(list) {
    await loadAttendCodes();
    const strip = (rows) => JSON.stringify((rows || []).map(({ order, ...c }) => c));
    if (strip(cache) === strip(list)) return;
    cache = list;
    try {
        await writeCodes(list);
    } catch (e) {
        console.warn('[AttendCodes] save failed', e);
    }
}

const SEPARATOR = ';';
const MAX_LENGTH = 255;

/** На записи может быть несколько кодов отсутствия: "Excused;Unexcused". */
export function splitCodes(code) {
    return String(code || '').split(SEPARATOR).map(c => c.trim()).filter(Boolean);
}

/** Бейдж кода. Удалённые коды в списке есть: они остаются на старых записях. */
export function attendShort(code) {
    return splitCodes(code).map(one => {
        const known = all().find(c => c.value === one);
        return known ? known.short : one.slice(0, 2).toUpperCase();
    }).join(',');
}

/** Ночная смена среди кодов: уволен, но работал ночью — это тоже ночь. */
export function hasNight(code) {
    return splitCodes(code).includes(NIGHT_CODE);
}

export function withoutNight(code) {
    return splitCodes(code).filter(c => c !== NIGHT_CODE).join(SEPARATOR);
}

export function withNight(code) {
    return nextAttendCode(code, [...splitCodes(code), NIGHT_CODE]);
}

/**
 * Значение после выбора в списке. Порядок как в справочнике, чтобы одинаковый
 * набор всегда был одной строкой.
 */
export function nextAttendCode(current, picked) {
    const list = Array.isArray(picked) ? picked : [picked];
    if (list.includes('')) return '';
    const codes = [...new Set(list)];
    const order = all().map(c => c.value);
    const rank = c => { const i = order.indexOf(c); return i < 0 ? order.length : i; };
    const joined = [...codes].sort((a, b) => rank(a) - rank(b)).join(SEPARATOR);
    // Поле и у нас, и в payroll — 255 символов: лишний код не добавляем.
    return joined.length > MAX_LENGTH ? (current || '') : joined;
}

/** Что предлагать в списке: активные коды плюс текущий, если его уже удалили. */
export function attendOptions(current) {
    const active = all().filter(c => c.active !== false);
    const removed = splitCodes(current).filter(code => !active.some(c => c.value === code));
    return [...active, ...removed.map(value => ({ value, short: attendShort(value), night: false }))];
}

/** Коды отсутствия для легенды: у каждого своя строка с описанием из админки. */
export function absenceCodes() {
    return attendOptions().filter(c => !c.night);
}

/** Описание Night work для легенды. */
export function nightDescription() {
    return all().find(c => c.night)?.description || NIGHT_TEXT;
}
